import { Component, EventEmitter, forwardRef, Input, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BsDatepickerConfig } from 'ngx-bootstrap/datepicker';
import { defineLocale } from 'ngx-bootstrap/chronos';
import { enGbLocale } from 'ngx-bootstrap/locale';
import { CommonService } from '@services/app.common.service';

// Registers the Monday-first locale definition only; doesn't switch the app-wide active locale.
// Each picker applies it per-instance via bsConfig.locale.
defineLocale('en-gb', enGbLocale);

/**
 * Output interface for byDateChange event.
 * Provides both the Date object and pre-formatted string.
 */
export interface JtDateValue {
  date: Date | null | undefined;       // Standard JavaScript Date for manipulation
  formatted: string;       // Pre-formatted string for API (default: "YYYY-MM-DD")
}

/**
 * JT Datepicker Component - Wrapper for ngx-bootstrap datepicker.
 * 
 * Features:
 * - Form control value is stored as FORMATTED STRING (ready for API)
 * - byDateChange event provides both Date object and formatted string
 * - Display format from client settings (localStorage)
 * - Output format configurable (defaults to YYYY-MM-DD)
 * - Works with ngModel and reactive forms
 * 
 * @example
 * <app-jt-datepicker
 *   formControlName="validFrom"
 *   (byDateChange)="onDateChange($event)">
 * </app-jt-datepicker>
 * 
 * // On save - just use form value directly:
 * // request.validFrom = this.form.controls.validFrom.value;  // "2024-12-06"
 */
@Component({
  selector: 'app-jt-datepicker',
  templateUrl: './jt-datepicker.component.html',
  styleUrls: ['./jt-datepicker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => JtDatepickerComponent),
      multi: true
    }
  ],
  standalone: false
})
export class JtDatepickerComponent implements ControlValueAccessor, OnInit {
  private readonly minDefaultDate = new Date(1900, 0, 1);
  private suppressNextEmit = false;
  private suppressNextEmitTimeout: any = null;
  // Optional inputs
  @Input() minDate: Date | undefined = undefined;
  @Input() maxDate: Date | undefined = undefined;
  @Input() testId: string = '';
  @Input() isDisabled: boolean = false;
  @Input() tooltipText: string = '';
  @Input() serverFormat: string = 'YYYY-MM-DD';  // Default API format
  @Input() placeholder: string = '';

  // Output emits both date and formatted string
  @Output() byDateChange = new EventEmitter<JtDateValue>();

  // Internal state
  // 'locale' isn't on BsDatepickerConfig's typings, but ngx-bootstrap reads it off the config per-instance.
  bsConfig: Partial<BsDatepickerConfig> & { locale?: string } = {};
  selectedDate: Date | undefined = undefined;
  formControlDisabled: boolean = false;

  // onChange callback - receives FORMATTED STRING (not Date)
  private onChange: (value: string) => void = () => { };
  private onTouched: () => void = () => { };

  constructor(private common: CommonService) { }

  private armSuppressNextEmit() {
    this.suppressNextEmit = true;

    // Safety: if ngx-bootstrap doesn't emit `bsValueChange` for a programmatic value write,
    // don't leave suppression enabled and accidentally swallow the next real user change.
    if (this.suppressNextEmitTimeout) {
      clearTimeout(this.suppressNextEmitTimeout);
    }
    this.suppressNextEmitTimeout = setTimeout(() => {
      this.suppressNextEmit = false;
      this.suppressNextEmitTimeout = null;
    }, 50);
  }

  ngOnInit(): void {
    // Get DISPLAY format from client settings
    const displayFormat = this.common.GetDateFormat()?.toUpperCase() || 'DD/MM/YYYY';

    this.bsConfig = {
      containerClass: 'theme-default',
      dateInputFormat: displayFormat,
      showWeekNumbers: false,
      adaptivePosition: true,
      showTodayButton: true,
      todayButtonLabel: 'Today',
      // en-gb gives a Monday - Sunday week for all clients.
      locale: 'en-gb'
    };
  }

  /**
   * Called when user picks a date from calendar.
   * Sets form control value to FORMATTED STRING.
   * Emits JtDateValue with both date and formatted string.
   */
  onDateSelected(date: Date | undefined): void {
    // ngx-bootstrap can trigger `bsValueChange` when the value is written programmatically via CVA.
    // Ignore that initial sync so consumers don't run "user changed date" logic on page load.
    if (this.suppressNextEmit) {
      this.suppressNextEmit = false;
      if (this.suppressNextEmitTimeout) {
        clearTimeout(this.suppressNextEmitTimeout);
        this.suppressNextEmitTimeout = null;
      }
      this.selectedDate = date;
      return;
    }

    this.selectedDate = date;

    const formatted = this.common.formatDate(date, this.serverFormat);
    this.onChange(formatted);  // Form control gets formatted string!
    this.onTouched();

    // Emit both date and formatted string
    this.byDateChange.emit({
      date: date,
      formatted: formatted
    });
  }

  // ---- ControlValueAccessor Implementation ----

  /**
   * Called when ngModel/formControl value changes from outside.
   * Handles Date objects and ISO strings from server.
   * NORMALIZES the form control value to serverFormat.
   */
  writeValue(value: Date | string | null): void {
    if (value === null || value === undefined || value === '') {
      this.armSuppressNextEmit();
      this.selectedDate = undefined;
      // Don't call onChange here to avoid infinite loop
    } else {
      // Parse to Date (handles both Date objects and ISO strings)
      const date = value instanceof Date ? value : new Date(value);

      if (this.isNullOrMinDate(date)) {
        this.armSuppressNextEmit();
        this.selectedDate = undefined;
        this.onChange('');
        return;
      }

      if (!isNaN(date.getTime())) {
        this.armSuppressNextEmit();
        this.selectedDate = date;  // For ngx-bootstrap display

        // Normalize form control to serverFormat
        const formatted = this.common.formatDate(date, this.serverFormat);

        // Only call onChange if value is different to avoid loops
        // const currentFormatted = this.common.formatDate(this.selectedDate, this.serverFormat); // Remove unused var
        if (formatted !== value) {
          // Value came in different format, normalize it
          setTimeout(() => this.onChange(formatted), 0);
        }
      } else {
        this.selectedDate = undefined;
      }
    }
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formControlDisabled = isDisabled;
  }

  /**
   * Accessible label for the calendar button/title. A whitespace-only placeholder is used to
   * blank out the visible input text, but it must not suppress the "Please select date"
   * fallback that screen readers and the title tooltip rely on.
   */
  get accessiblePlaceholder(): string {
    return this.placeholder?.trim() || 'Messages.SelectDate';
  }

  /**
   * Getter for formatted value on-demand.
   */
  get formattedValue(): string {
    return this.common.formatDate(this.selectedDate, this.serverFormat);
  }
  private isNullOrMinDate(date: Date | null | undefined): boolean {
    if (!date) return true;
    return (
      date.getFullYear() === this.minDefaultDate.getFullYear() &&
      date.getMonth() === this.minDefaultDate.getMonth() &&
      date.getDate() === this.minDefaultDate.getDate()
    );
  }
}
